/**
 * Rendelésszerkesztő — the staff editor of handover rev7 §11.11.
 *
 * §11.11.4: "The customer ordering page, opened in **staff mode** from the
 * console by a signed-in user. One page, one quantity engine: the
 * recommendation that staff see is the same calculation the customer sees."
 *
 * The recommendation is not calculated here. Every figure on this screen came
 * back from `staff_editor`'s *evaluate*, which re-ran the engine on the
 * server (§19.2 principle 2); the screen collects what staff typed, shows
 * what the server made of it, and sends the ticks back. That is why there is
 * no price arithmetic in this file and no copy of the fourteen override
 * codes: `public.override_codes` travels with the opening.
 *
 * The one deliberate difference from the customer page is that staff see
 * prices and portion counts. `docs/overnight_constraints.md` §1 forbids an
 * internal view **in the customer page**; this is the console, which is the
 * other side of that rule, and it shares no shell and no stylesheet with it.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { editorCall, ConsoleError } from '../api.ts'
import { asMoment, fmt, statusText, useConsoleData } from '../common.tsx'
import { useConsole } from '../context.ts'
import { stepBy, unitOrOne, wholeFieldValue } from '../productionUnits.ts'

/* ------------------------------------------------------------------ *
 * What the two endpoints send back
 * ------------------------------------------------------------------ */

interface ContextDish {
  id: string
  name_hu: string
  price_category: 'sos' | 'edes' | 'gyumolcs' | 'ital'
  is_drink: boolean
  cafe: boolean
  gluten_free_eligible: boolean
  lead_time_ok: boolean
  /**
   * Run 14 item 3: `dishes.production_unit`, how many portions come out of
   * one batch. It was in the payload all along — `submission_context` sends
   * `v_published_dishes` whole — and only this interface left it out, so the
   * editor's quantity fields had nothing to step by.
   */
  production_unit: number | null
  min_production: number | null
  net_price: number | null
}

interface Opening {
  staff: { email: string; name: string; roles: string[] }
  order: { id: string; ref: string; status: string; status_reason: string | null } | null
  live_version: number | null
  status: string | null
  payload: Record<string, unknown>
  draft: {
    based_on_version: number
    payload: Record<string, unknown>
    held_by: { email: string; name: string } | null
    held_since: string
    held_by_me: boolean
    stale: boolean
  } | null
  can_take_over: boolean
  change_requests: {
    id: string
    kind: string
    initiator: string
    received_at: string
    channel: string | null
    fee_tier: string | null
    fee_net: number | null
    fee_waived_note: string | null
    status: string
  }[]
  override_codes: { code: string; label_hu: string; rule_hu: string; touches_kitchen: boolean }[]
  /** Run 14 item 4: the roles that may tick a warning (`console_views`). */
  warning_roles?: string[]
  draft_expiry_bd: number | null
  context: {
    ok: boolean
    event_date: string
    zone: string
    dishes: ContextDish[]
    slots: { slot_id: string; starts_at: string; ends_at: string; offered: boolean }[]
  }
}

interface EvalLine {
  dish_id: string
  name_hu: string | null
  price_category: string
  is_gf_portion: boolean
  is_drink: boolean
  recommended_portions: number
  ordered_portions: number
  qty_source: 'recommendation' | 'staff'
  unit: string
  /** Run 14 item 3: the dish's production unit, as the server read it. */
  production_unit: number
  min_production: number | null
  line_gross: number
}

/** §11.11.5. `detail` names the dishes or the side a rule was measured on. */
interface EvalWarning {
  code: string
  rule_value: string
  order_value: string
  detail?: {
    side?: string
    category?: string
    dishes?: string[]
    lines?: { dish_id: string }[]
    dates?: string[]
  }
}

interface Evaluation {
  ok: boolean
  refusals: { code: string; message: string; dishId?: string }[]
  warnings: EvalWarning[]
  /**
   * What the **server** found unticked when it last evaluated. The counter
   * and the list below read `openWarnings` instead, which is this set less
   * the ticks made since; the server's own copy stays the authority for the
   * commit, which it re-checks for itself.
   */
  unacknowledged: { code: string }[]
  lines: EvalLine[]
  totals: { net: number; vat: number; gross: number; delivery_fee: number }
  adjustments: { kind: string; amount: number | null; pct: number | null; reason: string }[]
  per_guest: { savoury: number; sweet: number; total: number }
  diff: {
    lines: {
      dish_id: string
      name_hu: string | null
      change: string
      from_portions: number | null
      to_portions: number | null
    }[]
    from_gross: number
    to_gross: number
    gross_delta: number
  }
  live_version: number | null
  status: string | null
}

/* ------------------------------------------------------------------ *
 * The form's own state — what staff typed, and nothing derived
 * ------------------------------------------------------------------ */

interface Draft {
  event: Record<string, unknown>
  fulfilment: { kind: string; slot_id: string; postcode: string }
  basket: {
    savoury: string[]
    sweet: string[]
    /** Run 15 item 1(j): the quantities the customer set, kept as stored. */
    quantities: { dish_id: string; portions: number }[]
    fruit: string[]
    drinks: []
  }
  portions: { dish_id: string; is_gf_portion: boolean; portions: number }[]
  adjustments: { kind: string; amount: number | null; pct: number | null; reason: string }[]
  delivery_fee_override: number | null
  customer: Record<string, unknown>
  billing_address: Record<string, unknown> | null
  delivery_same_as_billing: boolean
  payment_method: string
  waive_fee: boolean
  acknowledged: { code: string; note: string }[]
}

const EMPTY: Draft = {
  event: {},
  fulfilment: { kind: 'delivery', slot_id: '', postcode: '' },
  basket: { savoury: [], sweet: [], quantities: [], fruit: [], drinks: [] },
  portions: [],
  adjustments: [],
  delivery_fee_override: null,
  customer: {},
  billing_address: null,
  delivery_same_as_billing: true,
  payment_method: 'card',
  waive_fee: false,
  acknowledged: [],
}

/** The opening payload, as the form holds it. Nothing is computed here. */
function draftFrom(opening: Opening): Draft {
  const payload = opening.payload ?? {}

  /* §11.11.2, *Callback conversion*: "the basket stored with the callback
     request". `public.callback_requests.basket` is the page's own basket
     object wrapped in an array (`submit_callback`, §5.7), so the sides come
     out of it rather than out of order lines there are none of. */
  if (Array.isArray(payload['basket'])) {
    const stored = ((payload['basket'] as Record<string, unknown>[])[0] ?? {}) as {
      savoury?: string[]
      sweet?: string[]
      quantities?: { dish_id: string; portions: number }[]
      fruit?: string[]
    }
    const event = (payload['event'] ?? {}) as Record<string, unknown>
    return {
      ...EMPTY,
      event,
      fulfilment: {
        kind: String((event['fulfilment'] as Record<string, string> | undefined)?.['kind'] ?? 'delivery'),
        slot_id: String((event['fulfilment'] as Record<string, string> | undefined)?.['slot_id'] ?? ''),
        postcode: String((event['fulfilment'] as Record<string, string> | undefined)?.['postcode'] ?? ''),
      },
      basket: {
        savoury: stored.savoury ?? [],
        sweet: stored.sweet ?? [],
        quantities: stored.quantities ?? [],
        fruit: stored.fruit ?? [],
        drinks: [],
      },
      customer: (payload['customer'] ?? {}) as Record<string, unknown>,
    }
  }

  const lines = (payload['lines'] ?? []) as {
    dish_id: string
    price_category: string
    is_gf_portion: boolean
    is_drink: boolean
    ordered_portions?: number
    qty_source?: string
  }[]
  const chosen = (category: string): string[] => [
    ...new Set(
      lines.filter((line) => line.price_category === category && !line.is_gf_portion)
        .map((line) => line.dish_id),
    ),
  ]
  const fulfilment = (payload['fulfilment'] ?? {}) as Record<string, string | null>
  return {
    ...EMPTY,
    event: (payload['event'] ?? {}) as Record<string, unknown>,
    fulfilment: {
      kind: fulfilment['kind'] ?? 'delivery',
      slot_id: fulfilment['slot_id'] ?? '',
      postcode: fulfilment['postcode'] ?? '',
    },
    basket: {
      savoury: chosen('sos'),
      sweet: chosen('edes'),
      /* Run 15 item 1(j): a line the customer counted out on the page is
         carried into the amendment as the quantity it is, so re-pricing
         recalculates the engine's own lines and leaves theirs alone. A dish
         in the gluten-free box is one dish and one figure, main and box
         together, which is how run 9 item 5 and run 14 item 3 measure it. */
      quantities: Object.entries(
        lines
          .filter((line) => line.qty_source === 'customer' && !line.is_drink)
          .reduce<Record<string, number>>((totals, line) => {
            totals[line.dish_id] = (totals[line.dish_id] ?? 0) + (line.ordered_portions ?? 0)
            return totals
          }, {}),
      ).map(([dish_id, portions]) => ({ dish_id, portions })),
      fruit: chosen('gyumolcs'),
      drinks: [],
    },
    adjustments: (payload['adjustments'] ?? []) as Draft['adjustments'],
    customer: (payload['customer'] ?? {}) as Record<string, unknown>,
    billing_address: (payload['billing_address'] ?? null) as Record<string, unknown> | null,
    delivery_same_as_billing: payload['delivery_address'] === null,
    payment_method: (payload['payment_method'] as string) ?? 'card',
  }
}

const OUTCOMES: { key: string; label: string; hint: string }[] = [
  { key: 'apply', label: 'Alkalmazás', hint: 'Azonos vagy alacsonyabb ár: új élő verzió, U10 (§11.11.7)' },
  { key: 'send_for_payment', label: 'Fizetésre küldés', hint: 'Magasabb ár: U19 a fizetési linkkel' },
  { key: 'send_as_proposal', label: 'Javaslatként küldés', hint: 'Planteen-kezdeményezésű változás: U4' },
  { key: 'discard', label: 'Piszkozat elvetése', hint: 'A piszkozat semmit nem változtat (§11.11.7)' },
]

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

export function Editor(): JSX.Element {
  const { config, session } = useConsole()
  const [search, setSearch] = useSearchParams()
  const orderId = search.get('order')
  const callbackId = search.get('callback')

  const [opening, setOpening] = useState<Opening | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [changeRequest, setChangeRequest] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  /* Run 14 item 3: where a typed quantity was raised to the next whole
     production unit, what it was and what it became. Shown beside the field
     it happened in, and cleared as soon as that field is typed in again, so
     the note is never about a number that is no longer there. */
  const [rounded, setRounded] = useState<Record<string, { from: number; to: number }>>({})

  const call = useCallback(
    async <T,>(action: string, args: Record<string, unknown>): Promise<T | null> => {
      setBusy(true)
      setError(null)
      try {
        return await editorCall<T>(action, args, { config, session })
      } catch (cause) {
        if (cause instanceof ConsoleError) {
          setError(cause.message)
          if (cause.code === 'unacknowledged_warnings' && cause.result !== null) {
            setEvaluation(cause.result as Evaluation)
          }
        } else setError('A művelet nem sikerült.')
        return null
      } finally {
        setBusy(false)
      }
    },
    [config, session],
  )

  const load = useCallback(async () => {
    if (orderId === null && callbackId === null) return
    setLoading(true)
    const answer = await call<Opening>('open', {
      order_id: orderId,
      callback_id: callbackId,
    })
    setLoading(false)
    if (answer === null) return
    setOpening(answer)
    setDraft(
      answer.draft !== null && answer.draft.held_by_me && !answer.draft.stale
        ? { ...EMPTY, ...(answer.draft.payload as unknown as Draft) }
        : draftFrom(answer),
    )
    setEvaluation(null)
  }, [orderId, callbackId, call])

  useEffect(() => {
    void load()
  }, [load])

  const versionRequest = useMemo(() => ({
    event: draft.event,
    fulfilment: draft.fulfilment,
    basket: draft.basket,
    portions: draft.portions,
    adjustments: draft.adjustments,
    delivery_fee_override: draft.delivery_fee_override,
    customer: draft.customer,
    billing_address: draft.billing_address,
    delivery_same_as_billing: draft.delivery_same_as_billing,
    payment_method: draft.payment_method,
    waive_fee: draft.waive_fee,
    acknowledged: draft.acknowledged,
  }), [draft])

  const setEvent = (key: string, value: unknown): void =>
    setDraft((previous) => ({ ...previous, event: { ...previous.event, [key]: value } }))

  const toggleDish = (dish: ContextDish): void =>
    setDraft((previous) => {
      const side = dish.price_category === 'edes' ? 'sweet' : dish.price_category === 'sos' ? 'savoury' : 'fruit'
      const current = previous.basket[side]
      return {
        ...previous,
        basket: {
          ...previous.basket,
          [side]: current.includes(dish.id)
            ? current.filter((id) => id !== dish.id)
            : [...current, dish.id],
        },
      }
    })

  const portionKey = (dishId: string, gf: boolean): string => `${dishId}|${gf ? 'gf' : 'main'}`

  const setPortions = (dishId: string, gf: boolean, value: string): void => {
    setRounded((previous) => {
      if (previous[portionKey(dishId, gf)] === undefined) return previous
      const next = { ...previous }
      delete next[portionKey(dishId, gf)]
      return next
    })
    setDraft((previous) => {
      const rest = previous.portions.filter(
        (entry) => !(entry.dish_id === dishId && entry.is_gf_portion === gf),
      )
      if (value.trim() === '') return { ...previous, portions: rest }
      return {
        ...previous,
        portions: [...rest, { dish_id: dishId, is_gf_portion: gf, portions: Number(value) }],
      }
    })
  }

  /**
   * Run 14 item 3. The quantity on a dish is written in whole production
   * units — the kitchen makes batches, and a wrap cut into eight has no
   * ninth piece. The figure a staff member types is raised to the next whole
   * unit and the change is shown; the +/− buttons move by one unit, so a
   * quantity reached with them is whole all the way.
   *
   * It is measured on the dish's **total**, its main line and its
   * gluten-free line together (run 9 item 5), and the shortfall goes on the
   * field that was edited. The server refuses an odd total whatever sends
   * it, so this is the console saying so early, not the rule itself.
   */
  const settlePortions = (dishId: string, gf: boolean, unit: number, typed: number | null): void => {
    const step = unitOrOne(unit)
    setDraft((previous) => {
      const mine = previous.portions.find(
        (entry) => entry.dish_id === dishId && entry.is_gf_portion === gf,
      )
      const other = previous.portions.find(
        (entry) => entry.dish_id === dishId && entry.is_gf_portion !== gf,
      )
      const otherRecommended = (evaluation?.lines ?? []).find(
        (line) => line.dish_id === dishId && line.is_gf_portion !== gf,
      )?.recommended_portions ?? 0
      const from = typed ?? mine?.portions ?? null
      if (from === null || !Number.isFinite(from)) return previous
      const to = wholeFieldValue(from, other?.portions ?? otherRecommended, step)
      if (to !== Math.round(from)) {
        setRounded((notes) => ({ ...notes, [portionKey(dishId, gf)]: { from: Math.round(from), to } }))
      }
      const rest = previous.portions.filter(
        (entry) => !(entry.dish_id === dishId && entry.is_gf_portion === gf),
      )
      return { ...previous, portions: [...rest, { dish_id: dishId, is_gf_portion: gf, portions: to }] }
    })
  }

  /** One press of − or +, by the dish's own production unit. */
  const stepPortions = (
    dishId: string,
    gf: boolean,
    unit: number,
    direction: 1 | -1,
    current: number,
  ): void => {
    setRounded((previous) => {
      if (previous[portionKey(dishId, gf)] === undefined) return previous
      const next = { ...previous }
      delete next[portionKey(dishId, gf)]
      return next
    })
    const to = stepBy(current, unit, direction)
    setDraft((previous) => {
      const rest = previous.portions.filter(
        (entry) => !(entry.dish_id === dishId && entry.is_gf_portion === gf),
      )
      return { ...previous, portions: [...rest, { dish_id: dishId, is_gf_portion: gf, portions: to }] }
    })
  }

  const toggleAck = (code: string): void =>
    setDraft((previous) => ({
      ...previous,
      acknowledged: previous.acknowledged.some((entry) => entry.code === code)
        ? previous.acknowledged.filter((entry) => entry.code === code ? false : true)
        : [...previous.acknowledged, { code, note: '' }],
    }))

  const setAckNote = (code: string, note: string): void =>
    setDraft((previous) => ({
      ...previous,
      acknowledged: previous.acknowledged.map((entry) =>
        entry.code === code ? { ...entry, note } : entry
      ),
    }))

  async function evaluateNow(): Promise<void> {
    const answer = await call<Evaluation>('evaluate', {
      order_id: orderId,
      version: versionRequest,
    })
    if (answer !== null) setEvaluation(answer)
  }

  async function saveDraft(takeOver = false): Promise<void> {
    const answer = await call<Record<string, unknown>>('save_draft', {
      order_id: orderId,
      based_on_version: opening?.live_version ?? null,
      payload: draft,
      take_over: takeOver,
    })
    if (answer !== null) await load()
  }

  async function commit(outcome: string): Promise<void> {
    const answer = await call<Record<string, unknown>>('commit', {
      order_id: orderId,
      outcome,
      version: versionRequest,
      reason,
      change_request_id: changeRequest,
      based_on_version: opening?.live_version ?? null,
    })
    if (answer === null) return
    setDone(
      outcome === 'discard'
        ? 'A piszkozatot elvetettük. A rendelésen semmi nem változott.'
        : `Kész: ${String(answer['effect'] ?? outcome)}. Verzió: ${String(answer['version'] ?? '—')}.`,
    )
    await load()
  }

  const codes = opening?.override_codes ?? []
  const labelOf = (code: string): string =>
    codes.find((entry) => entry.code === code)?.label_hu ?? code
  const ruleOf = (code: string): string =>
    codes.find((entry) => entry.code === code)?.rule_hu ?? ''
  const touchesKitchen = (code: string): boolean =>
    codes.find((entry) => entry.code === code)?.touches_kitchen === true

  /* ---- run 14 item 4: the warnings still waiting ----

     The server's `unacknowledged` is what it found the last time *Ellenőrzés*
     ran. Ticking a box does not re-run it, so the foot of the page went on
     saying *Még 2 figyelmeztetés jóváhagyásra vár* after both had been
     ticked, with the outcome buttons disabled and nothing on the page saying
     what to do next (Balázs, 14 October 2026). This is the same question
     asked of what is on screen now, so the count falls as the ticks are made.

     The server stays the authority: `commit` re-evaluates and refuses with
     `unacknowledged_warnings` if a warning the browser did not send back is
     still open. */
  const openWarnings = (evaluation?.warnings ?? []).filter(
    (warning) => !draft.acknowledged.some((entry) => entry.code === warning.code),
  )

  /* Who may tick one: the roles that may open this editor at all
     (`public.console_views`), because the tick is the only place a warning is
     cleared. `public.override_codes` holds no role of its own and none is
     invented here. */
  const clearingRoles = opening?.warning_roles ?? []
  const myRoles = opening?.staff.roles ?? []
  const mayClear =
    clearingRoles.length === 0 || myRoles.some((role) => clearingRoles.includes(role))
  const rolesSentence = clearingRoles.join(' vagy ')

  /** What a warning is about, in the words the console already uses. */
  const concerns = (warning: EvalWarning): string => {
    const dishNames = (ids: readonly string[]): string =>
      ids
        .map((id) =>
          opening?.context.dishes.find((dish) => dish.id === id)?.name_hu ??
          evaluation?.lines.find((line) => line.dish_id === id)?.name_hu ??
          id,
        )
        .join(', ')
    const detail = warning.detail
    if (detail?.lines !== undefined && detail.lines.length > 0) {
      return dishNames(detail.lines.map((line) => line.dish_id))
    }
    if (detail?.dishes !== undefined && detail.dishes.length > 0) return dishNames(detail.dishes)
    if (detail?.dates !== undefined && detail.dates.length > 0) return detail.dates.join(', ')
    if (detail?.category !== undefined) return detail.category === 'sos' ? 'a sós fogások' : 'az édes fogások'
    if (detail?.side !== undefined) return detail.side === 'sav' ? 'a sós oldal' : 'az édes oldal'
    return opening?.order?.ref ?? 'ez a rendelés'
  }

  if (orderId === null && callbackId === null) {
    return <PickOrder onPick={(id) => setSearch({ order: id })} />
  }

  const draftHeld = opening?.draft ?? null
  const blockedByOther = draftHeld !== null && !draftHeld.held_by_me

  return (
    <section data-testid="view-editor">
      <h2>Rendelésszerkesztő</h2>
      <p className="lead">
        §11.11: munkatársi mód. Minden mező szerkeszthető; minden oldalszabály figyelmeztetéssé
        válik, amelyet jóvá kell hagyni, és a jóváhagyás felülbírálatként a verzióra kerül.
        A javaslatot a kiszolgáló számolja: ugyanaz a motor, mint a vendégoldalon.
      </p>

      {callbackId !== null && (
        <p className="note gap" data-testid="editor-from-callback">
          §11.11.2: ez a szerkesztő egy visszahívási kérésből nyílt. A tárolt kosár és a
          rendezvény adatai betöltve; a rendelés a <em>Fizetésre küldés</em> eredménnyel jön
          létre. A visszahívás lezárása a visszahívások listájában marad.
        </p>
      )}
      {error !== null && <p className="note error" data-testid="editor-error">{error}</p>}
      {done !== null && <p className="note" data-testid="editor-done">{done}</p>}
      {loading && <p className="note">Betöltés…</p>}

      {opening !== null && (
        <>
          <div className="card" data-testid="editor-order">
            <h3>
              <Link to={`/staff/orders/${opening.order?.id ?? ''}`}>{opening.order?.ref ?? '—'}</Link>
              {' · '}
              {statusText(opening.status ?? '', opening.order?.status_reason)}
              {' · '}verzió {fmt.n(opening.live_version ?? 0)}
            </h3>

            {draftHeld !== null && (
              <p className={blockedByOther || draftHeld.stale ? 'note gap' : 'note'} data-testid="draft-state">
                {blockedByOther
                  ? `A piszkozatot ${draftHeld.held_by?.name ?? '?'} tartja, ${asMoment(draftHeld.held_since)} óta.`
                  : `A piszkozata mentve: ${asMoment(draftHeld.held_since)} óta nyitva.`}
                {draftHeld.stale && ' A rendelés időközben új verzióra váltott: töltse újra és ellenőrizze ismét (§11.11.8).'}
                {opening.draft_expiry_bd !== null &&
                  ` A ${fmt.n(opening.draft_expiry_bd)} munkanapja érintetlen piszkozatokat a rendszer elveti.`}
              </p>
            )}

            <div className="actions">
              <button type="button" disabled={busy} onClick={() => void saveDraft()} data-testid="save-draft">
                Piszkozat mentése
              </button>
              {blockedByOther && opening.can_take_over && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void saveDraft(true)}
                  data-testid="take-over"
                >
                  Piszkozat átvétele
                </button>
              )}
              <button type="button" disabled={busy} onClick={() => void load()} data-testid="reload">
                Újratöltés az élő verzióra
              </button>
            </div>
          </div>

          <ChangeRequests
            opening={opening}
            busy={busy}
            selected={changeRequest}
            onSelect={setChangeRequest}
            onLog={async (args) => {
              const answer = await call<{ change_request_id: string }>('log_change_request', {
                order_id: orderId,
                ...args,
              })
              if (answer !== null) {
                setChangeRequest(answer.change_request_id)
                await load()
              }
            }}
            onWaive={async (id, note) => {
              await call('waive_fee', { change_request_id: id, note })
              setDraft((previous) => ({ ...previous, waive_fee: true }))
              await load()
            }}
          />

          {/* ---- step 01, every field (§11.11.4) ---- */}
          <div className="card" data-testid="editor-event">
            <h3>01. A rendezvény</h3>
            <div className="grid">
              <div>
                <label htmlFor="ed-name">Rendezvény neve</label>
                <input
                  id="ed-name" type="text" data-testid="ed-name"
                  value={String(draft.event['name'] ?? '')}
                  onChange={(e) => setEvent('name', e.target.value)}
                />
                <label htmlFor="ed-date">Dátum</label>
                <input
                  id="ed-date" type="date" data-testid="ed-date"
                  value={String(draft.event['date'] ?? '')}
                  onChange={(e) => setEvent('date', e.target.value)}
                />
                <label htmlFor="ed-start">Kezdés</label>
                <input
                  id="ed-start" type="text" data-testid="ed-start" placeholder="12:00"
                  value={String(draft.event['start'] ?? '')}
                  onChange={(e) => setEvent('start', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="ed-guests">Vendégek száma</label>
                <input
                  id="ed-guests" type="number" min={1} data-testid="ed-guests"
                  value={String(draft.event['guests'] ?? '')}
                  onChange={(e) => setEvent('guests', Number(e.target.value))}
                />
                <label htmlFor="ed-gf">Ebből gluténmentes</label>
                <input
                  id="ed-gf" type="number" min={0} data-testid="ed-gf"
                  value={String(draft.event['gf_guests'] ?? 0)}
                  onChange={(e) => setEvent('gf_guests', Number(e.target.value))}
                />
                <label htmlFor="ed-profile">Mennyire étkűek</label>
                <select
                  id="ed-profile" data-testid="ed-profile"
                  value={String(draft.event['guest_profile'] ?? 'average')}
                  onChange={(e) => setEvent('guest_profile', e.target.value)}
                >
                  <option value="light">Kevésbé étkűek</option>
                  <option value="average">Átlagos</option>
                  <option value="hearty">Nagyétkűek</option>
                </select>
              </div>
              <div>
                <label htmlFor="ed-occasion">Alkalom</label>
                <input
                  id="ed-occasion" type="text" data-testid="ed-occasion"
                  value={String(draft.event['occasion'] ?? '')}
                  onChange={(e) => setEvent('occasion', e.target.value)}
                />
                <label htmlFor="ed-balance">Sós / édes arány</label>
                <select
                  id="ed-balance" data-testid="ed-balance"
                  value={String(draft.event['balance'] ?? 'rec')}
                  onChange={(e) => setEvent('balance', e.target.value)}
                >
                  <option value="sav">Több sós</option>
                  <option value="rec">Javasolt</option>
                  <option value="sw">Több édes</option>
                </select>
                <label htmlFor="ed-note">Étrendi megjegyzés</label>
                <textarea
                  id="ed-note" rows={2} data-testid="ed-note"
                  value={String(draft.event['dietary_note'] ?? '')}
                  onChange={(e) => setEvent('dietary_note', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="ed-fulfilment">Átvétel</label>
                <select
                  id="ed-fulfilment" data-testid="ed-fulfilment"
                  value={draft.fulfilment.kind}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, fulfilment: { ...p.fulfilment, kind: e.target.value } }))}
                >
                  <option value="delivery">Kiszállítás</option>
                  <option value="pickup">Személyes átvétel</option>
                </select>
                <label htmlFor="ed-slot">Sáv</label>
                <select
                  id="ed-slot" data-testid="ed-slot"
                  value={draft.fulfilment.slot_id}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, fulfilment: { ...p.fulfilment, slot_id: e.target.value } }))}
                >
                  <option value="">—</option>
                  {opening.context.slots.map((slot) => (
                    <option key={slot.slot_id} value={slot.slot_id}>
                      {slot.starts_at}–{slot.ends_at}{slot.offered ? '' : ' (nem kínált)'}
                    </option>
                  ))}
                </select>
                <label htmlFor="ed-postcode">Irányítószám</label>
                <input
                  id="ed-postcode" type="text" data-testid="ed-postcode"
                  value={draft.fulfilment.postcode}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, fulfilment: { ...p.fulfilment, postcode: e.target.value } }))}
                />
              </div>
            </div>
          </div>

          {/* ---- the menu (§11.11.4: any number of published dishes) ---- */}
          <div className="card" data-testid="editor-menu">
            <h3>02. A menü</h3>
            <p className="note">
              §11.11.1: a választék a publikációé. Szabad szöveges fogás és nem ellenőrzött
              allergénű fogás nincs — ez az egyetlen kapu, amely munkatársi módban is kapu marad.
            </p>
            <table>
              <thead>
                <tr>
                  <th>Fogás</th>
                  <th>Kategória</th>
                  <th className="num">Javaslat</th>
                  <th className="num">Adag</th>
                  <th>Megjegyzés</th>
                </tr>
              </thead>
              <tbody>
                {opening.context.dishes
                  .filter((dish) => dish.price_category === 'sos' || dish.price_category === 'edes')
                  .map((dish) => {
                    const picked = draft.basket.savoury.includes(dish.id) ||
                      draft.basket.sweet.includes(dish.id)
                    const line = evaluation?.lines.find(
                      (entry) => entry.dish_id === dish.id && !entry.is_gf_portion,
                    )
                    const set = draft.portions.find(
                      (entry) => entry.dish_id === dish.id && !entry.is_gf_portion,
                    )
                    return (
                      <tr key={dish.id} data-testid={`dish-${dish.id}`}>
                        <td>
                          <label>
                            <input
                              type="checkbox"
                              checked={picked}
                              onChange={() => toggleDish(dish)}
                              data-testid={`pick-${dish.id}`}
                            />{' '}
                            {dish.name_hu}
                          </label>
                        </td>
                        <td>{dish.price_category === 'sos' ? 'Sós' : 'Édes'}</td>
                        <td className="num" data-testid={`rec-${dish.id}`}>
                          {line === undefined ? '—' : fmt.n(line.recommended_portions)}
                        </td>
                        <td className="num">
                          <QtyField
                            testId={`qty-${dish.id}`}
                            unit={unitOrOne(dish.production_unit)}
                            disabled={!picked}
                            placeholder={line === undefined ? '' : String(line.recommended_portions)}
                            value={set === undefined ? '' : String(set.portions)}
                            current={set?.portions ?? line?.recommended_portions ?? 0}
                            note={rounded[`${dish.id}|main`]}
                            onType={(value) => setPortions(dish.id, false, value)}
                            onSettle={(typed) =>
                              settlePortions(dish.id, false, unitOrOne(dish.production_unit), typed)}
                            onStep={(direction, current) =>
                              stepPortions(dish.id, false, unitOrOne(dish.production_unit), direction, current)}
                          />
                        </td>
                        <td>
                          {!dish.lead_time_ok && <span className="tag warn">átfutási idő</span>}
                          {!dish.cafe && <span className="tag">nem kávézós</span>}
                          {dish.gluten_free_eligible && <span className="tag ok">gluténmentes</span>}
                          {dish.min_production !== null && (
                            <span className="tag">min. {fmt.n(dish.min_production)}</span>
                          )}
                          {unitOrOne(dish.production_unit) > 1 && (
                            <span className="tag" data-testid={`unit-${dish.id}`}>
                              {fmt.n(unitOrOne(dish.production_unit))} adagos egység
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>

            {(evaluation?.lines ?? []).some((line) => line.is_gf_portion) && (
              <>
                <h3 style={{ marginTop: 14 }}>Gluténmentes adagok</h3>
                <p className="note">
                  §11.11.4: a gluténmentes adagok összetételét a Planteen állítja össze (§6.7),
                  a darabszámuk itt fogásonként szerkeszthető.
                </p>
                <table>
                  <thead>
                    <tr><th>Fogás</th><th className="num">Javaslat</th><th className="num">Adag</th></tr>
                  </thead>
                  <tbody>
                    {evaluation!.lines.filter((line) => line.is_gf_portion).map((line) => {
                      const set = draft.portions.find(
                        (entry) => entry.dish_id === line.dish_id && entry.is_gf_portion,
                      )
                      const unit = unitOrOne(
                        opening.context.dishes.find((dish) => dish.id === line.dish_id)?.production_unit
                          ?? line.production_unit,
                      )
                      return (
                        <tr key={`gf-${line.dish_id}`} data-testid={`gf-${line.dish_id}`}>
                          <td>
                            {line.name_hu ?? line.dish_id}
                            {unit > 1 && (
                              <span className="tag" data-testid={`gfunit-${line.dish_id}`}>
                                {fmt.n(unit)} adagos egység
                              </span>
                            )}
                          </td>
                          <td className="num">{fmt.n(line.recommended_portions)}</td>
                          <td className="num">
                            <QtyField
                              testId={`gfqty-${line.dish_id}`}
                              unit={unit}
                              disabled={false}
                              placeholder={String(line.recommended_portions)}
                              value={set === undefined ? '' : String(set.portions)}
                              current={set?.portions ?? line.recommended_portions}
                              note={rounded[`${line.dish_id}|gf`]}
                              onType={(value) => setPortions(line.dish_id, true, value)}
                              onSettle={(typed) => settlePortions(line.dish_id, true, unit, typed)}
                              onStep={(direction, current) =>
                                stepPortions(line.dish_id, true, unit, direction, current)}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>

          <Money
            draft={draft}
            setDraft={setDraft}
            evaluation={evaluation}
          />

          <Contact draft={draft} setDraft={setDraft} />

          {/* ---- §11.11.7 step 3 ---- */}
          <div className="card" data-testid="editor-evaluate">
            <h3>Ellenőrzés</h3>
            <p className="note">
              §11.11.7: a kiszolgáló újrafuttat minden szabályt a mai katalógusra, paraméterekre
              és kapacitásra, és visszaadja az eltérést, az árhatást és a jóváhagyandó
              figyelmeztetéseket.
            </p>
            <div className="actions">
              <button
                type="button" className="primary" disabled={busy}
                onClick={() => void evaluateNow()} data-testid="evaluate"
              >
                Ellenőrzés
              </button>
            </div>
          </div>

          {evaluation !== null && (
            <>
              {evaluation.refusals.length > 0 && (
                <div className="card" data-testid="editor-refusals">
                  <h3>Ez így nem menthető</h3>
                  <ul>
                    {evaluation.refusals.map((refusal) => (
                      <li key={`${refusal.code}-${refusal.dishId ?? ''}`}>{refusal.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="card" data-testid="editor-warnings">
                <h3>Figyelmeztetések (§11.11.5)</h3>
                {evaluation.warnings.length === 0
                  ? <p className="note" data-testid="no-warnings">Nincs figyelmeztetés: ez a verzió semmilyen szabálytól nem tér el.</p>
                  : (
                    <table>
                      <thead>
                        <tr><th>Jóváhagyás</th><th>Eltérés</th><th>Szabály</th><th>Ez a rendelés</th><th>Indoklás</th></tr>
                      </thead>
                      <tbody>
                        {evaluation.warnings.map((warning) => {
                          const ack = draft.acknowledged.find((entry) => entry.code === warning.code)
                          return (
                            <tr key={warning.code} id={`warning-${warning.code}`} data-testid={`warning-${warning.code}`}>
                              <td>
                                <input
                                  type="checkbox"
                                  id={`ack-${warning.code}`}
                                  checked={ack !== undefined}
                                  onChange={() => toggleAck(warning.code)}
                                  data-testid={`ack-${warning.code}`}
                                  aria-label={labelOf(warning.code)}
                                  disabled={!mayClear}
                                />
                              </td>
                              <td>
                                {labelOf(warning.code)}
                                {touchesKitchen(warning.code) && (
                                  <div className="tag warn">a konyha is látja (K1)</div>
                                )}
                              </td>
                              <td>{warning.rule_value}</td>
                              <td>{warning.order_value}</td>
                              <td>
                                <input
                                  type="text"
                                  value={ack?.note ?? ''}
                                  disabled={ack === undefined}
                                  onChange={(e) => setAckNote(warning.code, e.target.value)}
                                  data-testid={`note-${warning.code}`}
                                />
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  )}
              </div>

              <div className="card" data-testid="editor-diff">
                <h3>Eltérés az élő verzióhoz képest</h3>
                {evaluation.diff.lines.length === 0
                  ? <p className="note">A tételek nem változtak.</p>
                  : (
                    <table>
                      <thead>
                        <tr><th>Fogás</th><th>Változás</th><th className="num">Eddig</th><th className="num">Ezután</th></tr>
                      </thead>
                      <tbody>
                        {evaluation.diff.lines.map((line) => (
                          <tr key={`${line.dish_id}-${line.change}`} data-testid={`diff-${line.dish_id}`}>
                            <td>{line.name_hu ?? line.dish_id}</td>
                            <td>{{
                              added: 'új tétel',
                              removed: 'törölve',
                              quantity: 'mennyiség',
                              price: 'ár',
                            }[line.change] ?? line.change}</td>
                            <td className="num">{line.from_portions === null ? '—' : fmt.n(line.from_portions)}</td>
                            <td className="num">{line.to_portions === null ? '—' : fmt.n(line.to_portions)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                <dl className="facts" style={{ marginTop: 10 }}>
                  <dt>Eddigi bruttó</dt><dd>{fmt.money(evaluation.diff.from_gross)}</dd>
                  <dt>Új bruttó</dt><dd data-testid="diff-to-gross">{fmt.money(evaluation.diff.to_gross)}</dd>
                  <dt>Árhatás</dt>
                  <dd data-testid="diff-delta">
                    {evaluation.diff.gross_delta > 0 ? '+' : ''}{fmt.money(evaluation.diff.gross_delta)}
                  </dd>
                  <dt>Vendégenként</dt>
                  <dd>
                    {fmt.n(evaluation.per_guest.savoury, 1)} sós · {fmt.n(evaluation.per_guest.sweet, 1)} édes
                  </dd>
                </dl>
              </div>

              <div className="card" data-testid="editor-outcome">
                <h3>Eredmény (§11.11.7)</h3>
                <label htmlFor="ed-reason">Indoklás — ez kerül a verzióra</label>
                <textarea
                  id="ed-reason" rows={2} value={reason}
                  onChange={(e) => setReason(e.target.value)} data-testid="ed-reason"
                />
                {openWarnings.length > 0 && (
                  <div className="note gap" data-testid="unacknowledged">
                    <p style={{ margin: 0 }}>
                      <b>Még {fmt.n(openWarnings.length)} figyelmeztetés jóváhagyásra vár.</b>{' '}
                      {mayClear
                        ? 'Mindegyiknél pipálja ki a jóváhagyást; a lista alább mutatja, hol.'
                        : `Ezeket ${rolesSentence} szerepkör hagyhatja jóvá; az Ön szerepköre nem.`}
                    </p>
                    <ul data-testid="open-warnings" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                      {openWarnings.map((warning) => (
                        <li key={warning.code} data-testid={`open-warning-${warning.code}`}>
                          <b>{labelOf(warning.code)}</b>
                          {ruleOf(warning.code) === '' ? null : <> — {ruleOf(warning.code)}</>}
                          <br />
                          Ezt érinti: {concerns(warning)}. A szabály: {warning.rule_value}; ezen a
                          rendelésen: {warning.order_value}.
                          <br />
                          {mayClear ? (
                            <a
                              href={`#warning-${warning.code}`}
                              data-testid={`goto-${warning.code}`}
                              onClick={(event) => {
                                event.preventDefault()
                                const row = document.getElementById(`warning-${warning.code}`)
                                row?.scrollIntoView({ block: 'center' })
                                document.getElementById(`ack-${warning.code}`)?.focus()
                              }}
                            >
                              Ugrás a jóváhagyáshoz
                            </a>
                          ) : (
                            <span data-testid={`cannot-${warning.code}`}>
                              Jóváhagyhatja: {rolesSentence}.
                            </span>
                          )}
                          {mayClear && <> · Jóváhagyhatja: {rolesSentence}.</>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="actions">
                  {OUTCOMES.map((outcome) => (
                    <button
                      key={outcome.key}
                      type="button"
                      className={outcome.key === 'discard' ? 'danger' : 'primary'}
                      title={outcome.hint}
                      disabled={busy || (outcome.key !== 'discard' && !evaluation.ok)}
                      onClick={() => void commit(outcome.key)}
                      data-testid={`outcome-${outcome.key}`}
                    >
                      {outcome.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ *
 * The pieces
 * ------------------------------------------------------------------ */

/**
 * One quantity on a dish line — run 14 item 3.
 *
 * − and + move by the dish's own production unit, so a wrap cut into eight
 * goes 8, 16, 24 and never nine. A figure typed straight into the field is
 * taken as it is while it is being typed — a staff member heading for 128
 * passes through 12 — and raised to the next whole unit when they leave the
 * field, with a line beside it saying what it was and what it became.
 *
 * A dish whose production unit is one behaves exactly as the plain number
 * field did: ±1, and nothing to round.
 */
function QtyField(props: {
  testId: string
  unit: number
  disabled: boolean
  placeholder: string
  value: string
  /** What the line holds now — the typed figure, else the recommendation. */
  current: number
  note: { from: number; to: number } | undefined
  onType: (value: string) => void
  onSettle: (typed: number | null) => void
  onStep: (direction: 1 | -1, current: number) => void
}): JSX.Element {
  const { testId, unit, disabled, placeholder, value, current, note } = props
  return (
    <>
      <span className="qtystep">
        <button
          type="button"
          data-testid={`${testId}-minus`}
          aria-label={`Egy egységgel kevesebb (${unit} adag)`}
          disabled={disabled}
          onClick={() => props.onStep(-1, current)}
        >
          −
        </button>
        <input
          type="number" min={0} step={unit} style={{ maxWidth: '7em' }}
          data-testid={testId}
          placeholder={placeholder}
          value={value}
          onChange={(e) => props.onType(e.target.value)}
          onBlur={(e) => props.onSettle(e.target.value.trim() === '' ? null : Number(e.target.value))}
          disabled={disabled}
        />
        <button
          type="button"
          data-testid={`${testId}-plus`}
          aria-label={`Egy egységgel több (${unit} adag)`}
          disabled={disabled}
          onClick={() => props.onStep(1, current)}
        >
          +
        </button>
      </span>
      {note !== undefined && (
        <div className="note gap" data-testid={`${testId}-rounded`}>
          {fmt.n(note.from)} → {fmt.n(note.to)} adag: ez a fogás {fmt.n(unit)} adagos egységekben készül.
        </div>
      )}
    </>
  )
}

function PickOrder({ onPick }: { onPick: (id: string) => void }): JSX.Element {
  const { data, error, loading } = useConsoleData<{
    orders: { id: string; ref: string; event_date: string; customer_name: string; status: string }[]
  }>('orders', { limit: 30 })

  return (
    <section data-testid="view-editor">
      <h2>Rendelésszerkesztő</h2>
      <p className="lead">
        §11.11.2: a szerkesztő egy rendelésből nyílik. Válasszon egyet, vagy nyissa meg a
        rendelés lapjáról.
      </p>
      {error !== null && <p className="note error">{error}</p>}
      {loading && <p className="note">Betöltés…</p>}
      {data !== null && (
        <table>
          <thead><tr><th>Rendelés</th><th>Rendezvény</th><th>Vendég</th><th /></tr></thead>
          <tbody>
            {data.orders.map((order) => (
              <tr key={order.id}>
                <td>{order.ref}</td>
                <td>{order.event_date}</td>
                <td>{order.customer_name}</td>
                <td>
                  <button type="button" onClick={() => onPick(order.id)} data-testid={`open-${order.ref}`}>
                    Szerkesztés
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function ChangeRequests(props: {
  opening: Opening
  busy: boolean
  selected: string | null
  onSelect: (id: string | null) => void
  onLog: (args: Record<string, unknown>) => Promise<void>
  onWaive: (id: string, note: string) => Promise<void>
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [receivedAt, setReceivedAt] = useState('')
  const [channel, setChannel] = useState('email')
  const [kind, setKind] = useState('modification')
  const [initiator, setInitiator] = useState('customer')
  const [reasonCode, setReasonCode] = useState('other')
  const [text, setText] = useState('')
  const [waiveNote, setWaiveNote] = useState('')

  return (
    <div className="card" data-testid="editor-requests">
      <h3>Módosítási kérés (§11.8)</h3>
      <p className="note">
        §11.8: a kérés attól számít, amikor <strong>megérkezett</strong>, nem attól, amikor
        rögzítettük. A díjsáv ebből az időpontból jön. A <strong>Díj</strong> oszlop nettó
        összeg. A vevő a saját díjalapja szerint látja (Barna döntése, 2026. 10. 05.,
        8. pont): a céges vevő a nettó ár százalékaként, a magánvevő a rendelés
        végösszegének százalékaként — a felszámított összeg mindkét esetben ugyanannyi,
        bruttó.
      </p>
      <p className="note" data-testid="fee-base-note">
        Barna döntése, 2026. 10. 15.: a <strong>módosítás</strong> díjalapja a módosított
        tételek értéke — tételenként a darabszám változása szorozva a tétel egységárával,
        a rendelés saját árain —, nem a teljes rendelés. A lemondás díjalapja változatlanul
        a teljes rendelés. Ezért a módosítási kérés díja a rögzítéskor még üres (−): akkor
        áll elő, amikor a verziót alkalmazzuk.
      </p>

      {props.opening.change_requests.length > 0 && (
        <table>
          <thead>
            <tr><th /><th>Beérkezett</th><th>Csatorna</th><th>Típus</th><th>Díjsáv</th><th className="num">Díj (nettó)</th><th>Állapot</th></tr>
          </thead>
          <tbody>
            {props.opening.change_requests.map((request) => (
              <tr key={request.id} data-testid={`request-${request.id}`}>
                <td>
                  <input
                    type="radio" name="cr" checked={props.selected === request.id}
                    onChange={() => props.onSelect(request.id)}
                    aria-label="A verzió ehhez a kéréshez tartozik"
                  />
                </td>
                <td>{asMoment(request.received_at)}</td>
                <td>{request.channel ?? '—'}</td>
                <td>{request.kind === 'cancellation' ? 'lemondás' : 'módosítás'} · {request.initiator}</td>
                <td data-testid={`fee-tier-${request.id}`}>
                  {{
                    free: 'díjmentes',
                    before_48h: 'véglegesítés után',
                    within_48h: '48 órán belül',
                  }[request.fee_tier ?? ''] ?? '—'}
                </td>
                {/* Run 16 item 3(e): a modification is logged with its tier
                    and no amount — the lines it changes are not known until
                    staff draft it — so an empty fee reads as empty rather
                    than as nothing to pay. */}
                <td className="num" data-testid={`fee-net-${request.id}`}>
                  {request.fee_net === null ? '—' : fmt.money(request.fee_net)}
                </td>
                <td>
                  {request.status}
                  {request.fee_waived_note !== null && (
                    <div className="tag warn">díj elengedve: {request.fee_waived_note}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="actions">
        <button type="button" onClick={() => setOpen(!open)} data-testid="log-request-open">
          Kérés rögzítése
        </button>
        {props.selected !== null && (
          <>
            <input
              type="text" placeholder="A díj elengedésének indoka"
              value={waiveNote} onChange={(e) => setWaiveNote(e.target.value)}
              data-testid="waive-note" style={{ maxWidth: 260 }}
            />
            <button
              type="button"
              disabled={props.busy || waiveNote.trim() === ''}
              onClick={() => void props.onWaive(props.selected!, waiveNote)}
              data-testid="waive-fee"
            >
              Díj elengedése
            </button>
          </>
        )}
      </div>

      {open && (
        <div className="grid" style={{ marginTop: 10 }}>
          <div>
            <label htmlFor="cr-at">Mikor érkezett</label>
            <input
              id="cr-at" type="text" placeholder="2026-10-20T13:50"
              value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)}
              data-testid="cr-received-at"
            />
            <label htmlFor="cr-channel">Csatorna</label>
            <select id="cr-channel" value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="cr-channel">
              <option value="email">e-mail</option>
              <option value="phone">telefon, írásban megerősítve</option>
              <option value="reply">válasz egy rendelési levélre</option>
            </select>
          </div>
          <div>
            <label htmlFor="cr-kind">Típus</label>
            <select id="cr-kind" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="cr-kind">
              <option value="modification">módosítás</option>
              <option value="cancellation">lemondás</option>
            </select>
            <label htmlFor="cr-initiator">Kezdeményező</label>
            <select
              id="cr-initiator" value={initiator}
              onChange={(e) => setInitiator(e.target.value)} data-testid="cr-initiator"
            >
              <option value="customer">a vendég</option>
              <option value="planteen">a Planteen</option>
            </select>
            {initiator === 'planteen' && (
              <>
                <label htmlFor="cr-reason">Ok</label>
                <select
                  id="cr-reason" value={reasonCode}
                  onChange={(e) => setReasonCode(e.target.value)} data-testid="cr-reason-code"
                >
                  <option value="dish_unavailable">fogás nem elérhető</option>
                  <option value="allergen_correction">allergén-javítás</option>
                  <option value="capacity">kapacitás</option>
                  <option value="delivery">szállítás</option>
                  <option value="force_majeure">vis maior</option>
                  <option value="other">egyéb</option>
                </select>
              </>
            )}
          </div>
          <div>
            <label htmlFor="cr-text">Mit kér a vendég</label>
            <textarea id="cr-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} data-testid="cr-text" />
            <div className="actions">
              <button
                type="button" className="primary" disabled={props.busy || receivedAt.trim() === ''}
                data-testid="cr-save"
                onClick={() =>
                  void props.onLog({
                    kind,
                    initiator,
                    received_at: receivedAt,
                    channel,
                    request_text: text,
                    reason_code: initiator === 'planteen' ? reasonCode : null,
                  }).then(() => setOpen(false))}
              >
                Rögzítés
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Money(props: {
  draft: Draft
  setDraft: (update: (previous: Draft) => Draft) => void
  evaluation: Evaluation | null
}): JSX.Element {
  const { draft, setDraft, evaluation } = props
  return (
    <div className="card" data-testid="editor-money">
      <h3>Ár (§11.11.4)</h3>
      <p className="note">
        A fogások ára a katalógusé, hogy a számlasorok mindig a publikációval egyezzenek.
        Minden ártól való eltérés korrekciós tétel, indoklással.
      </p>

      <table>
        <thead>
          <tr><th>Korrekció</th><th className="num">Összeg</th><th className="num">%</th><th>Indoklás</th><th /></tr>
        </thead>
        <tbody>
          {draft.adjustments.map((adjustment, index) => (
            <tr key={index} data-testid={`adjustment-${index}`}>
              <td>
                <select
                  value={adjustment.kind}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      adjustments: p.adjustments.map((entry, i) =>
                        i === index ? { ...entry, kind: e.target.value } : entry),
                    }))}
                  data-testid={`adj-kind-${index}`}
                >
                  <option value="discount">kedvezmény</option>
                  <option value="surcharge">felár</option>
                </select>
              </td>
              <td className="num">
                <input
                  type="number" style={{ maxWidth: '9em' }} data-testid={`adj-amount-${index}`}
                  value={adjustment.amount === null ? '' : String(adjustment.amount)}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      adjustments: p.adjustments.map((entry, i) =>
                        i === index
                          ? { ...entry, amount: e.target.value === '' ? null : Number(e.target.value), pct: null }
                          : entry),
                    }))}
                />
              </td>
              <td className="num">
                <input
                  type="number" style={{ maxWidth: '6em' }} data-testid={`adj-pct-${index}`}
                  value={adjustment.pct === null ? '' : String(adjustment.pct)}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      adjustments: p.adjustments.map((entry, i) =>
                        i === index
                          ? { ...entry, pct: e.target.value === '' ? null : Number(e.target.value), amount: null }
                          : entry),
                    }))}
                />
              </td>
              <td>
                <input
                  type="text" data-testid={`adj-reason-${index}`}
                  value={adjustment.reason}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      adjustments: p.adjustments.map((entry, i) =>
                        i === index ? { ...entry, reason: e.target.value } : entry),
                    }))}
                />
              </td>
              <td>
                <button
                  type="button" className="danger" data-testid={`adj-remove-${index}`}
                  onClick={() =>
                    setDraft((p) => ({
                      ...p,
                      adjustments: p.adjustments.filter((_, i) => i !== index),
                    }))}
                >
                  Törlés
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="actions">
        <button
          type="button" data-testid="adj-add"
          onClick={() =>
            setDraft((p) => ({
              ...p,
              adjustments: [...p.adjustments, { kind: 'discount', amount: null, pct: null, reason: '' }],
            }))}
        >
          Korrekciós tétel
        </button>
      </div>

      <div className="grid" style={{ marginTop: 10 }}>
        <div>
          <label htmlFor="ed-fee">Szállítási díj, bruttó — üresen a zóna díja</label>
          <input
            id="ed-fee" type="number" data-testid="ed-delivery-fee"
            value={draft.delivery_fee_override === null ? '' : String(draft.delivery_fee_override)}
            onChange={(e) =>
              setDraft((p) => ({
                ...p,
                delivery_fee_override: e.target.value === '' ? null : Number(e.target.value),
              }))}
          />
          <label htmlFor="ed-payment">Fizetési mód — bármelyik vevőnek</label>
          <select
            id="ed-payment" data-testid="ed-payment-method"
            value={draft.payment_method}
            onChange={(e) => setDraft((p) => ({ ...p, payment_method: e.target.value }))}
          >
            <option value="card">bankkártya</option>
            <option value="transfer">átutalás</option>
          </select>
        </div>
        {evaluation !== null && (
          <dl className="facts" data-testid="editor-totals">
            <dt>Nettó</dt><dd>{fmt.money(evaluation.totals.net)}</dd>
            <dt>ÁFA</dt><dd>{fmt.money(evaluation.totals.vat)}</dd>
            <dt>Bruttó</dt><dd data-testid="total-gross">{fmt.money(evaluation.totals.gross)}</dd>
            <dt>Szállítás</dt><dd>{fmt.money(evaluation.totals.delivery_fee)}</dd>
          </dl>
        )}
      </div>
    </div>
  )
}

function Contact(props: {
  draft: Draft
  setDraft: (update: (previous: Draft) => Draft) => void
}): JSX.Element {
  const { draft, setDraft } = props
  const customer = draft.customer as Record<string, string | boolean | undefined>
  const billing = (draft.billing_address ?? {}) as Record<string, string | undefined>
  const set = (key: string, value: unknown): void =>
    setDraft((p) => ({ ...p, customer: { ...p.customer, [key]: value } }))
  const setBilling = (key: string, value: string): void =>
    setDraft((p) => ({ ...p, billing_address: { ...(p.billing_address ?? {}), [key]: value } }))

  return (
    <div className="card" data-testid="editor-contact">
      <h3>03. Kapcsolat és számlázás</h3>
      <p className="note">
        §11.11.1: a név, az e-mail-cím és a telefonszám — céges vevőnél a cégnév és az adószám is
        — kötelező. Ez az egyetlen másik szabály, amely munkatársi módban sem válik
        figyelmeztetéssé.
      </p>
      <div className="grid">
        <div>
          <label htmlFor="c-last">Vezetéknév</label>
          <input id="c-last" type="text" data-testid="c-last"
            value={String(customer['last_name'] ?? '')} onChange={(e) => set('last_name', e.target.value)} />
          <label htmlFor="c-first">Keresztnév</label>
          <input id="c-first" type="text" data-testid="c-first"
            value={String(customer['first_name'] ?? '')} onChange={(e) => set('first_name', e.target.value)} />
          <label htmlFor="c-email">E-mail</label>
          <input id="c-email" type="text" data-testid="c-email"
            value={String(customer['email'] ?? '')} onChange={(e) => set('email', e.target.value)} />
          <label htmlFor="c-phone">Telefon</label>
          <input id="c-phone" type="text" data-testid="c-phone"
            value={String(customer['phone'] ?? '')} onChange={(e) => set('phone', e.target.value)} />
        </div>
        <div>
          <label>
            <input
              type="checkbox" data-testid="c-business"
              checked={customer['is_business'] === true}
              onChange={(e) => set('is_business', e.target.checked)}
            />{' '}
            Céges vevő
          </label>
          <label htmlFor="c-company">Cégnév</label>
          <input id="c-company" type="text" data-testid="c-company"
            value={String(customer['company'] ?? '')} onChange={(e) => set('company', e.target.value)} />
          <label htmlFor="c-vat">Adószám</label>
          <input id="c-vat" type="text" data-testid="c-vat"
            value={String(customer['vat_number'] ?? '')} onChange={(e) => set('vat_number', e.target.value)} />
        </div>
        <div>
          <label htmlFor="b-postcode">Számlázási irányítószám</label>
          <input id="b-postcode" type="text" data-testid="b-postcode"
            value={String(billing['postcode'] ?? '')} onChange={(e) => setBilling('postcode', e.target.value)} />
          <label htmlFor="b-town">Település</label>
          <input id="b-town" type="text" data-testid="b-town"
            value={String(billing['town'] ?? '')} onChange={(e) => setBilling('town', e.target.value)} />
          <label htmlFor="b-street">Utca és házszám</label>
          <input id="b-street" type="text" data-testid="b-street"
            value={String(billing['street_and_number'] ?? '')}
            onChange={(e) => setBilling('street_and_number', e.target.value)} />
        </div>
      </div>
    </div>
  )
}
